'use client';

import dynamic from 'next/dynamic';
import Image, { type ImageProps } from 'next/image';
import { type BaseReactPlayerProps } from 'react-player/base';

import { ImageWpSizesSchema } from '~/lib/api/fragments/image';
import { OptionalMediaSchema } from '~/lib/api/fragments/media';
import { mediaHasRatio } from '~/lib/utils/media';

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

interface Props extends OptionalMediaSchema {
  imageProps?: Omit<ImageProps, 'src' | 'alt'> & {
    wpSize?: ImageWpSizesSchema;
  };
  videoProps?: React.ComponentProps<'video'>;
  reactPlayerProps?: BaseReactPlayerProps & { url?: never };
}

const Media: React.FC<Props> = ({
  media,
  imageProps,
  videoProps,
  reactPlayerProps,
}) => {
  switch (media.type) {
    case 'image': {
      if (media.image && media.image.node.sourceUrl) {
        const { wpSize, ...imagePropsRest } = imageProps || {};

        const src = wpSize
          ? (media.image.node.mediaDetails.sizes?.find(
              ({ name }) => name === wpSize
            )?.sourceUrl ?? media.image.node.sourceUrl)
          : media.image.node.sourceUrl;

        return (
          <Image
            src={src}
            alt={media.image.node.altText}
            placeholder={media.image.node.blurDataUrl ? 'blur' : 'empty'}
            blurDataURL={media.image.node.blurDataUrl || undefined}
            unoptimized
            {...imagePropsRest}
          />
        );
      }

      break;
    }

    case 'video': {
      if (media.video) {
        return (
          <video {...videoProps}>
            <source
              src={media.video.node.mediaItemUrl}
              type={media.video.node.mimeType}
            />
          </video>
        );
      }

      break;
    }

    case 'youtube':
    case 'vimeo': {
      const url =
        media.type === 'youtube'
          ? `https://youtube.com/watch?v=${media.youtubeId}`
          : `https://vimeo.com/${media.vimeoId}`;

      const propStyles = reactPlayerProps?.style || ({} as React.CSSProperties);

      return (
        <ReactPlayer
          url={url}
          {...reactPlayerProps}
          style={{
            ...propStyles,

            aspectRatio: mediaHasRatio(media)
              ? [media.ratio.width, media.ratio.height].join('/')
              : '16/9',
          }}
        />
      );
    }
  }

  return null;
};

export default Media;
